import React, { useCallback, useEffect, useMemo } from 'react';
import './ScreenSelector.css';
import { RingLoader } from 'react-spinners';
import {
    ALL_SCREENS_BUNDLE_USD,
    SLIDES_PER_SCREEN_DAY,
    BILLING_DAYS_PER_PERIOD,
    PLAYS_PER_SCREEN_MONTH,
    SINGLE_SCREEN_PRICE_USD,
    calculateCampaignTotalUsd,
    calculateEffectivePerScreenUsd,
    totalPlaysForScreens,
    normalizeScreenIds,
} from '../../../../utils/advertiserScreenPricing';
import { truncateDisplay, SCREEN_DISPLAY_LIMITS } from '../../../../utils/screenDisplayUtils';
import { useFleetScreensQuery, useScreenPricingQuery } from '../../../../query/dashboardQueries';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function ScreenSelector({ selectedScreenIds, onSelectionChange, selectionError }) {
    const { data: fleetData, isLoading: fleetLoading, error: fleetError } = useFleetScreensQuery();
    const { data: pricingData, isLoading: pricingLoading, error: pricingError } = useScreenPricingQuery();

    const screens = fleetData?.screens || [];
    const billableScreenIds = useMemo(
        () => normalizeScreenIds(pricingData?.billableScreenIds || []),
        [pricingData?.billableScreenIds]
    );
    const fleetScreenCount = pricingData?.fleetScreenCount || billableScreenIds.length || 1;
    const loading = fleetLoading || pricingLoading;
    const loadError = fleetError
        ? 'Could not load screens. Please refresh the page.'
        : pricingError
            ? 'Could not load screen pricing. Please refresh the page.'
            : '';

    const selectedSet = useMemo(
        () => new Set(normalizeScreenIds(selectedScreenIds)),
        [selectedScreenIds]
    );

    const billableScreens = useMemo(() => {
        const billable = new Set(billableScreenIds);
        return screens.filter((s) => billable.has(s.screenId));
    }, [screens, billableScreenIds]);

    const selectedCount = selectedSet.size;
    const fleetSize = Math.max(1, fleetScreenCount || billableScreens.length || 1);
    const totalUsd = calculateCampaignTotalUsd(selectedCount, fleetSize);
    const effectivePerScreen = calculateEffectivePerScreenUsd(selectedCount, fleetSize);
    const totalPlays = totalPlaysForScreens(selectedCount);

    const notifyParent = useCallback((nextIds) => {
        const ids = normalizeScreenIds(nextIds);
        const fleet = Math.max(1, fleetScreenCount || billableScreens.length || 1);
        onSelectionChange({
            screenIds: ids,
            screenCount: ids.length,
            fleetSize: fleet,
            totalUsd: calculateCampaignTotalUsd(ids.length, fleet),
            effectivePerScreenUsd: calculateEffectivePerScreenUsd(ids.length, fleet),
            totalGuaranteedPlays: totalPlaysForScreens(ids.length),
        });
    }, [onSelectionChange, fleetScreenCount, billableScreens.length]);

    useEffect(() => {
        if (loading || billableScreenIds.length === 0) {
            return;
        }
        const billable = new Set(billableScreenIds);
        const pruned = normalizeScreenIds(selectedScreenIds).filter((id) => billable.has(id));
        if (pruned.length !== normalizeScreenIds(selectedScreenIds).length) {
            notifyParent(pruned);
        }
    }, [loading, billableScreenIds, selectedScreenIds, notifyParent]);

    function toggleScreen(screenId) {
        const id = parseInt(screenId, 10);
        const next = new Set(selectedSet);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        notifyParent([...next]);
    }

    function selectAll() {
        const ids = billableScreens.map((s) => s.screenId);
        notifyParent(ids);
    }

    function clearAll() {
        notifyParent([]);
    }

    if (loading) {
        return (
            <div className="screenSelectorSection">
                <div className="screenSelectorLoading">
                    <RingLoader color="#C52F00" loading size={40} aria-label="Loading screens" />
                </div>
            </div>
        );
    }

    return (
        <div className="screenSelectorSection">
            <div className="screenSelectorHeader">
                <span className="newCampaignInputTitle" style={{ marginBottom: 0 }}>Choose screens</span>
                <span className="screenSelectorSubtitle" style={{ margin: 0 }}>
                    {SLIDES_PER_SCREEN_DAY} plays/screen/day × {BILLING_DAYS_PER_PERIOD} days = {PLAYS_PER_SCREEN_MONTH.toLocaleString()} plays per screen / month
                </span>
            </div>
            <p className="screenSelectorSubtitle">
                Select Invert screens to advertise on. Pricing starts at {currency.format(SINGLE_SCREEN_PRICE_USD)} for one screen
                and scales to {currency.format(ALL_SCREENS_BUNDLE_USD)} when you select all {fleetSize} Invert screen{fleetSize === 1 ? '' : 's'}.
                ATM partner screens are not available for direct purchase yet.
            </p>

            <div className="screenSelectorActions">
                <button type="button" className="screenSelectorLinkBtn" onClick={selectAll} disabled={billableScreens.length === 0}>
                    Select all ({billableScreens.length})
                </button>
                <button type="button" className="screenSelectorLinkBtn" onClick={clearAll} disabled={selectedCount === 0}>
                    Clear selection
                </button>
            </div>

            {loadError && <p className="screenSelectorError">{loadError}</p>}

            <div className="screenSelectorGrid" role="group" aria-label="Available screens">
                {billableScreens.map((screen) => {
                    const checked = selectedSet.has(screen.screenId);
                    return (
                        <label
                            key={screen.screenId}
                            className={`screenSelectorCard ${checked ? 'selected' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleScreen(screen.screenId)}
                            />
                            <div className="screenSelectorCardBody">
                                <div className="screenSelectorCardTitle">Screen #{screen.screenId}</div>
                                <div className="screenSelectorCardMeta">
                                    {truncateDisplay(screen.address || screen.locationDescription || 'Location on file', SCREEN_DISPLAY_LIMITS.addressLine)}
                                </div>
                            </div>
                        </label>
                    );
                })}
            </div>

            {billableScreens.length === 0 && !loadError && (
                <p className="screenSelectorError">No screens are available for advertising right now.</p>
            )}

            <div className="screenSelectorSummary">
                <div className="screenSelectorSummaryRow">
                    <span>Screens selected</span>
                    <span>{selectedCount}</span>
                </div>
                {selectedCount > 0 && (
                    <>
                        <div className="screenSelectorSummaryRow">
                            <span>Effective rate per screen</span>
                            <span>{currency.format(effectivePerScreen)}</span>
                        </div>
                        <div className="screenSelectorSummaryRow">
                            <span>Total guaranteed plays</span>
                            <span>{totalPlays.toLocaleString()}</span>
                        </div>
                    </>
                )}
                <div className="screenSelectorSummaryTotal">
                    <span>Monthly campaign fee</span>
                    <span>{currency.format(totalUsd)}</span>
                </div>
            </div>

            {selectionError && <p className="screenSelectorError">{selectionError}</p>}
        </div>
    );
}
